import SearchIcon from "@mui/icons-material/Search";
import { Box } from "@mui/material";
import { GridColDef, GridSortModel } from "@mui/x-data-grid";
import { useMemo } from "react";
import ChipSelector from "@/components/ChipSelector";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import Flex from "@/components/Flex";
import RenderWithTooltip from "@/components/RenderWithTooltip";
import { GlobalTableHeightWrapper } from "@/components/GlobalTableHeightWrapper";
import SearchInput from "@/components/SearchInput";
import StatusChip from "@/components/StatusChip";
import Text from "@/components/Text";
import ActionMenu from "@/components/app/ActionMenu";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { applyDefaultColumnSettings, DGT_Pagination } from "@/utils/utils";
import {
  GROUP_LIST_ACTION_FIELD,
  GROUP_LIST_COLUMN_LABEL,
  GROUP_LIST_FILTER_CHIPS,
  GROUP_LIST_LABEL,
  GROUP_LIST_SEARCH_WIDTH,
  GROUP_LIST_SORTABLE_FIELDS,
  GROUP_STATUS_LABEL,
  GROUP_TYPE_OPTIONS,
  THERAPY_CATEGORY_OPTIONS,
} from "../group-visit-constant";
import {
  GroupDetail,
  GroupListFilter,
  GroupRowAction,
} from "../group-visit-types";
import {
  formatEnrolledPatients,
  formatMinToRun,
  formatProviderNames,
  getGroupRowActions,
  getGroupStatusVariant,
  getOptionLabel,
} from "../group-visit-utils";
// import ScheduleTEmpGroupVisit from "./ScheduleTEmpGroupVisit";

export type GroupListProps = {
  groups: GroupDetail[];
  isLoading?: boolean;
  searchKey: string;
  onSearch: (value: string) => void;
  statusFilter: GroupListFilter;
  onStatusFilterChange: (value: GroupListFilter) => void;
  pagination: DGT_Pagination;
  onPaginationChange: (value: DGT_Pagination) => void;
  sortModel: GridSortModel;
  onSortModelChange: (value: GridSortModel) => void;
  totalCount: number;
  onCreateGroup: () => void;
  onRowAction: (action: GroupRowAction, group: GroupDetail) => void;
  onRowClick: (group: GroupDetail) => void;
  canCreate?: boolean;
};

/**
 * @author Rajkumar Rathod
 */
const GroupList = ({
  groups,
  isLoading,
  searchKey,
  onSearch,
  statusFilter,
  onStatusFilterChange,
  pagination,
  onPaginationChange,
  sortModel,
  onSortModelChange,
  totalCount,
  onCreateGroup,
  onRowAction,
  onRowClick,
  canCreate = true,
}: GroupListProps) => {
  const columns = useMemo<GridColDef<GroupDetail>[]>(
    () =>
      applyDefaultColumnSettings<GroupDetail>(
        [
          {
            field: "groupName",
            headerName: GROUP_LIST_COLUMN_LABEL.groupName,
            flex: 1,
            minWidth: 180,
            renderCell: ({ row }) => (
              <Box
                sx={{
                  minWidth: 0,
                  color: CHART_COLORS.primary,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <RenderWithTooltip value={row.groupName} />
              </Box>
            ),
          },
          {
            field: "primaryTherapistName",
            headerName: GROUP_LIST_COLUMN_LABEL.providerName,
            flex: 1,
            minWidth: 160,
            renderCell: ({ row }) => (
              <RenderWithTooltip
                value={formatProviderNames(
                  row.primaryTherapistName,
                  row.coTherapistName
                )}
              />
            ),
          },
          {
            field: "groupType",
            headerName: GROUP_LIST_COLUMN_LABEL.groupType,
            flex: 1,
            minWidth: 160,
            renderCell: ({ row }) => (
              <RenderWithTooltip
                value={getOptionLabel(GROUP_TYPE_OPTIONS, row.groupType) ?? "-"}
              />
            ),
          },
          {
            field: "therapyCategory",
            headerName: GROUP_LIST_COLUMN_LABEL.therapyCategory,
            flex: 1,
            minWidth: 160,
            renderCell: ({ row }) => (
              <RenderWithTooltip
                value={
                  getOptionLabel(
                    THERAPY_CATEGORY_OPTIONS,
                    row.therapyCategory
                  ) ?? "-"
                }
              />
            ),
          },
          {
            field: "enrolledPatientCount",
            headerName: GROUP_LIST_COLUMN_LABEL.enrolledPatients,
            flex: 0.6,
            minWidth: 130,
            renderCell: ({ row }) => (
              <Text isTabletext>
                {formatEnrolledPatients(
                  row.enrolledPatientCount,
                  row.maxCapacity
                )}
              </Text>
            ),
          },
          {
            field: "minToRun",
            headerName: GROUP_LIST_COLUMN_LABEL.minPatientReq,
            flex: 0.6,
            minWidth: 130,
            renderCell: ({ row }) => (
              <Text isTabletext>{formatMinToRun(row.minToRun)}</Text>
            ),
          },
          {
            field: "status",
            headerName: GROUP_LIST_COLUMN_LABEL.status,
            flex: 0.6,
            minWidth: 120,
            renderCell: ({ row }) =>
              row.status ? (
                <StatusChip
                  label={GROUP_STATUS_LABEL[row.status]}
                  variant={getGroupStatusVariant(row.status)}
                />
              ) : null,
          },
          {
            field: GROUP_LIST_ACTION_FIELD,
            headerName: GROUP_LIST_COLUMN_LABEL.action,
            width: 80,
            renderCell: ({ row }) => (
              <ActionMenu
                options={getGroupRowActions(row.status)}
                onActionClick={(action) =>
                  onRowAction(action as GroupRowAction, row)
                }
              />
            ),
          },
        ],
        GROUP_LIST_SORTABLE_FIELDS
      ),
    [onRowAction]
  );

  return (
    <Flex flexDirection="column" height="100%" gap={1}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <Text
          component="h1"
          {...CHART_TYPOGRAPHY.body3}
          fontWeight={CHART_TYPOGRAPHY.weight.medium}
          c={CHART_COLORS.textDarker}
        >
          {GROUP_LIST_LABEL.title}
        </Text>

        {canCreate && (
          <Flex gap={1}>
            <StyledButtonV2
              label={GROUP_LIST_LABEL.createGroup}
              variant="primary"
              shouldAddIcon
              onClick={onCreateGroup}
            />
          </Flex>
        )}
      </Flex>

      <Flex alignItems="center" gap={1.5} flexWrap="wrap">
        <SearchInput
          searchKey={searchKey}
          onSearch={onSearch}
          onClear={() => onSearch("")}
          placeholder={GROUP_LIST_LABEL.searchPlaceholder}
          name="groupSearch"
          startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
          sx={{ width: GROUP_LIST_SEARCH_WIDTH, flexShrink: 0 }}
        />

        <ChipSelector
          items={GROUP_LIST_FILTER_CHIPS}
          activeId={statusFilter}
          onSelect={(id) => onStatusFilterChange(id as GroupListFilter)}
          ariaLabel={GROUP_LIST_LABEL.statusFilterLabel}
        />
      </Flex>

      <GlobalTableHeightWrapper small>
        <CustomDataGridV2
          rows={groups}
          columns={columns}
          getRowId={(row) => row.uuid ?? row.groupName ?? ""}
          loading={isLoading}
          rowCount={totalCount}
          paginationModel={pagination}
          onPaginationModelChange={onPaginationChange}
          sortModel={sortModel}
          onSortModelChange={(model) => onSortModelChange([...model])}
          onCellClick={({ field, row }) => {
            if (field !== GROUP_LIST_ACTION_FIELD) onRowClick(row);
          }}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          searchString={searchKey}
        />
      </GlobalTableHeightWrapper>
    </Flex>
  );
};

export default GroupList;
